import {useLazyQuery, useMutation} from '@apollo/client'
import {FormEvent, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {EDIT_CONTACT} from '../../../gql/mutations/contactsMutation'
import {useDebounce} from '../../../../../_metronic/helpers'
import Select from 'react-select'
import {parseId} from '../../../helpers'
import {GET_ALL_USERS_FILTER} from '../../../gql/queries/usersQuery'
import {IResAllUsers} from '../../oneUser/oneUserTypes'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
}

export const ModalAssignContact: React.FC<IModalProps> = ({id, isOpen, onClose, refetch}) => {
  const intl = useIntl()
  const [gqlGetMembers, {data: dataUsers, loading: loadingUsers}] = useLazyQuery<
    IResAllUsers,
    {name: string; requireNpn: boolean}
  >(GET_ALL_USERS_FILTER)

  const [gqlUpdateContact, {data: dataUpdate, loading, error: errorUpdate}] = useMutation(EDIT_CONTACT)

  useEffect(() => {
    if (!dataUpdate) return

    if (dataUpdate.updateContact.success) {
      toast.success(
        intl.formatMessage({
          id: 'contacts.assign.toast.success',
          defaultMessage: 'Contact reassigned successfully',
        })
      )
      refetch()
      onClose()
    } else if (dataUpdate.updateContact.errors && dataUpdate.updateContact.errors.length > 0) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: dataUpdate.updateContact.errors[0].message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  useEffect(() => {
    if (errorUpdate) {
      toast.error(
        intl.formatMessage(
          {
            id: 'contacts.assign.toast.error',
            defaultMessage: 'Error assigning contact: {message}',
          },
          {message: errorUpdate.message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdate])

  const [agentId, setAgent] = useState<string | null>(null)
  const [inputSelect, setInputSelect] = useState('')
  const valueDeb = useDebounce(inputSelect, 500)
  const [options, setOptions] = useState<{label: string; value: string}[]>([])

  useEffect(() => {
    if (!valueDeb) {
      setOptions([])
      return
    }
    if (!dataUsers) return
    setOptions(
      dataUsers.allUsers.edges.map(({node}) => ({
        label: `${node.firstName} ${node.lastName} - ${node.email}${node.npn ? ` (${node.npn})` : ''}`,
        value: node.id,
      }))
    )
  }, [dataUsers, valueDeb])

  useEffect(() => {
    if (!valueDeb) return
    gqlGetMembers({variables: {name: valueDeb, requireNpn: false}})
  }, [gqlGetMembers, valueDeb])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    gqlUpdateContact({
      variables: {
        id: parseId(id),
        contactData: {userId: Number(parseId(agentId || ''))},
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='contacts.assign.title' defaultMessage='Assign Contact' />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='assignContactForm' onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>
              <FormattedMessage
                id='contacts.assign.selectUser'
                defaultMessage='Select the user to assign this contact'
              />
            </Form.Label>
            <Select
              isLoading={loadingUsers}
              loadingMessage={() =>
                intl.formatMessage({
                  id: 'contacts.assign.loadingMembers',
                  defaultMessage: 'loading members',
                })
              }
              placeholder={intl.formatMessage({
                id: 'contacts.assign.searchPlaceholder',
                defaultMessage: 'Search and select ...',
              })}
              options={options}
              inputValue={inputSelect}
              onInputChange={setInputSelect}
              onChange={(selectedOption) => {
                const userId = selectedOption?.value || 0
                setAgent('' + parseId(userId))
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button size='sm' variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          size='sm'
          variant='primary'
          type='submit'
          form='assignContactForm'
          disabled={!agentId || loading}
        >
          {loading && <Spinner animation='border' size='sm' className='me-2' />}
          <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
